// Themed due-date picker used across the Communication list row and the ticket
// detail view. Combines shadcn Popover + react-day-picker under the hood so the
// dark theme is guaranteed (no OS-native calendar chrome). Also exposes a small
// editable text input at the top so admins who prefer typing 2026-08-14 don't
// have to click through months.
import React, { useMemo, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Calendar, CalendarDays, X, Loader2 } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

const DP_STYLE = `
.rdp-tickets { --rdp-cell-size: 34px; --rdp-accent-color: #10b981; --rdp-background-color: #0f1611; margin: 0; }
.rdp-tickets .rdp-caption_label { color: #e2e8f0; font-size: 12px; font-weight: 600; }
.rdp-tickets .rdp-nav_button { color: #94a3b8; background: transparent; }
.rdp-tickets .rdp-nav_button:hover { background: rgba(16,185,129,0.15); color: #10b981; }
.rdp-tickets .rdp-head_cell { color: #64748b; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
.rdp-tickets .rdp-day { color: #cbd5e1; font-size: 12px; border-radius: 6px; }
.rdp-tickets .rdp-day:hover:not(.rdp-day_selected):not([disabled]) { background: rgba(16,185,129,0.12); color: #a7f3d0; }
.rdp-tickets .rdp-day_today:not(.rdp-day_selected) { color: #10b981; font-weight: 700; border: 1px solid rgba(16,185,129,0.4); }
.rdp-tickets .rdp-day_selected, .rdp-tickets .rdp-day_selected:focus, .rdp-tickets .rdp-day_selected:hover { background: #10b981; color: #06231a; font-weight: 700; }
.rdp-tickets .rdp-day_outside { color: #475569; opacity: 0.5; }
.rdp-tickets .rdp-day_disabled { color: #334155; opacity: 0.4; }
`;

// Parse a YYYY-MM-DD (or ISO with time) into a Date at local midnight
function parseISODate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}
function toISODate(date) {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function humanLabel(iso) {
  const dt = parseISODate(iso);
  if (!dt) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.floor((dt.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return { text: 'Today', tone: 'today' };
  if (diff === 1) return { text: 'Tomorrow', tone: 'soon' };
  if (diff === -1) return { text: 'Yesterday', tone: 'overdue' };
  const label = dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: dt.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
  if (diff < 0) return { text: label, tone: 'overdue' };
  if (diff <= 2) return { text: label, tone: 'soon' };
  return { text: label, tone: 'normal' };
}
const TONE_CLS = {
  overdue: 'text-red-400',
  today: 'text-amber-300 font-semibold',
  soon: 'text-amber-200',
  normal: 'text-slate-200',
  muted: 'text-slate-500 italic',
};

/**
 * Themed due-date picker.
 * Props:
 *   value    string | null   ISO date (YYYY-MM-DD)
 *   disabled boolean
 *   compact  boolean         renders the tight list-row variant (no border)
 *   testId   string          data-testid for the trigger
 *   onChange (iso|null) => Promise
 */
export const DueDatePicker = ({ value, disabled, compact = false, testId = 'due-date-picker', onChange }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [typed, setTyped] = useState('');
  const selected = useMemo(() => parseISODate(value), [value]);
  const label = value ? humanLabel(value) : { text: compact ? '—' : 'Set due date', tone: 'muted' };

  const commit = async (iso) => {
    if ((iso || null) === (value || null)) { setOpen(false); return; }
    setSaving(true);
    try { await onChange(iso || null); setOpen(false); }
    finally { setSaving(false); }
  };

  const handleTextChange = (e) => {
    const v = e.target.value.trim();
    setTyped(v);
  };
  const handleTextCommit = () => {
    if (!typed) return; // empty → don't commit, use Clear button instead
    const dt = parseISODate(typed);
    if (dt) commit(toISODate(dt));
  };

  return (
    <>
      <style>{DP_STYLE}</style>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setTyped(value || ''); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled || saving}
            onClick={(e) => e.stopPropagation()}
            className={
              compact
                ? 'flex items-center gap-1.5 px-1 py-1 rounded-md hover:bg-emerald-500/10 text-left w-full min-w-0'
                : 'flex items-center gap-2 px-2 py-1 rounded-md border border-emerald-900/30 hover:border-emerald-500/50 text-xs min-w-[150px]'
            }
            style={compact ? undefined : { background: '#0f1611' }}
            data-testid={testId}
          >
            <CalendarDays className={`w-3.5 h-3.5 flex-shrink-0 ${value ? 'text-emerald-400' : 'text-slate-500'}`} />
            <span className={`text-xs truncate flex-1 ${saving ? 'text-slate-500' : TONE_CLS[label.tone]}`}>
              {saving ? 'Saving…' : label.text}
            </span>
            {value && !saving && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); commit(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); commit(null); } }}
                className="text-slate-500 hover:text-red-400 cursor-pointer"
                title="Clear due date"
                data-testid={`${testId}-clear`}
              >
                <X className="w-3 h-3" />
              </span>
            )}
            {saving && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-auto p-0 border-emerald-900/40 shadow-2xl"
          style={{ background: '#0a0f0d', color: '#e2e8f0' }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testId}-popover`}
        >
          {/* Editable text input synced with the calendar selection */}
          <div className="px-3 pt-3 pb-2 border-b border-emerald-900/40">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Due date</label>
            <div className="mt-1 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-slate-500" />
              <input
                type="text"
                inputMode="numeric"
                autoFocus
                placeholder="YYYY-MM-DD"
                value={typed}
                onChange={handleTextChange}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleTextCommit(); } }}
                onBlur={handleTextCommit}
                className="flex-1 bg-transparent text-white text-xs focus:outline-none placeholder-slate-600 font-mono"
                data-testid={`${testId}-input`}
              />
            </div>
          </div>

          <DayPicker
            mode="single"
            className="rdp-tickets p-2"
            selected={selected || undefined}
            onSelect={(d) => commit(d ? toISODate(d) : null)}
            defaultMonth={selected || new Date()}
            captionLayout="dropdown-buttons"
            fromYear={new Date().getFullYear() - 1}
            toYear={new Date().getFullYear() + 5}
            weekStartsOn={1}
          />

          <div className="flex items-center justify-between px-3 py-2 border-t border-emerald-900/40 text-[11px]">
            <button
              type="button"
              onClick={() => commit(toISODate(new Date()))}
              className="text-emerald-400 hover:text-emerald-300 font-medium"
              data-testid={`${testId}-today`}
            >
              Today
            </button>
            {value && (
              <button
                type="button"
                onClick={() => commit(null)}
                className="text-slate-400 hover:text-red-400"
                data-testid={`${testId}-clear-footer`}
              >
                Clear
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default DueDatePicker;
